import type { BlockPerimeters } from "building-types/blocks/blockTypes";
import { StreetOrientationMode } from "building-types/building/streetOrientationMode";
import type { Roof, RoofBlocks } from "building-types/roofs/roofSystemTypes";
import type { RoofSettings } from "building-types/settings/roofSettings";
import { RoofBoundaryType } from "building-types/settings/settingsTypesEnum";
import { assertUnreachable } from "common/utils/assertUnreachable";
import { isVirtualRoofBlock, roofBlockIds } from "doc/roofSystemTools";
import { planeNormal } from "geom/plane";
import type { Plane } from "geom/planeTypes";
import { polyLine2IsRectangle } from "geom/polyLine2";

export function isValidRoofTypeForEdgeOverrides(roofSettings: RoofSettings, roofBlocks: RoofBlocks): boolean {
  const isVirtualRoof = isVirtualRoofBlock(roofBlocks);

  if (!isVirtualRoof) {
    return false;
  }

  const { boundaryType = RoofBoundaryType.BOUNDING_BOX } = roofSettings;
  const { perimeter } = roofBlocks;

  return boundaryType === RoofBoundaryType.CLIP_TO_PERIMETER && !polyLine2IsRectangle(perimeter);
}

export function convertDormerDistParam(
  dist: number | undefined,
  edgeLen: number,
  defaultValue: number = 0.0,
  normalizedDist: boolean,
): number {
  if (dist === undefined) {
    dist = defaultValue;
  }

  if (normalizedDist) {
    dist *= edgeLen;
  }

  if (dist < 0) {
    dist = edgeLen + dist;
  }

  return Math.min(Math.max(dist, 0.0), edgeLen);
}

export function includeElementBasedOnOrientation(
  plane: Plane | null,
  orientation: StreetOrientationMode,
  tol: number = 0,
): boolean {
  if (!plane) {
    return false;
  }

  const [nX, nY] = planeNormal(plane);

  switch (orientation) {
    case StreetOrientationMode.ALL_FACES: {
      return true;
    }
    case StreetOrientationMode.FRONT_FACES: {
      return Math.abs(nY) > Math.abs(nX) - tol && nY < 0;
    }
    case StreetOrientationMode.FRONT_AND_SIDE_FACES: {
      return Math.abs(nX) > Math.abs(nY) - tol || nY < 0;
    }
    case StreetOrientationMode.FRONT_AND_BACK_FACES: {
      return Math.abs(nY) > Math.abs(nX) - tol;
    }
    case StreetOrientationMode.NO_FACES: {
      return false;
    }
    default: {
      assertUnreachable(orientation);
    }
  }
}

export function filterRoofBlocks(blocks: string[], blockPerimeters: BlockPerimeters): string[] {
  const subRoofBlocks: string[] = [];

  for (let i = 0, l = blocks.length; i < l; i++) {
    const blockId = blocks[i];
    if (!blockPerimeters[blockId]) {
      continue;
    }

    subRoofBlocks.push(blockId);
  }

  return subRoofBlocks;
}

export function getSubRoofBlocks(subRoof: Roof, blockPerimeters: BlockPerimeters): string[] {
  const blockIds = roofBlockIds(subRoof.blocks);
  if (blockIds.length > 0) {
    return filterRoofBlocks(blockIds, blockPerimeters);
  }
  return [];
}
